import React, { useMemo, useState } from 'react';
import { HiOutlineEnvelope, HiOutlineEye, HiOutlineEyeSlash, HiOutlineLockClosed, HiOutlineMoon, HiOutlineSun } from 'react-icons/hi2';
import { Link, useNavigate } from 'react-router-dom';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { toast } from 'react-toastify';
import init from './firebase';
import logo from './assets/logo.png';
import { useTheme } from './context/ThemeContext';

const Login = () => {
  const navigate = useNavigate();
  const { isDarkMode, toggleTheme } = useTheme();
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [data, setData] = useState({
    email: '',
    password: '',
  });

  const errors = useMemo(
    () => ({
      email: !data.email.trim() ? 'Email address is required.' : '',
      password: !data.password ? 'Password is required.' : '',
    }),
    [data.email, data.password],
  );

  const hasErrors = Boolean(errors.email || errors.password);

  const formHandler = (event) => {
    const { name, value } = event.target;
    setData((currentData) => ({ ...currentData, [name]: value }));
  };

  const loginHandler = async (event) => {
    event.preventDefault();
    setSubmitted(true);

    if (hasErrors) {
      toast.error('Please complete the highlighted fields.');
      return;
    }

    try {
      setIsSubmitting(true);
      const user = await signInWithEmailAndPassword(init.auth, data.email, data.password);

      if (user) {
        toast.success('Successfully logged in.');
        navigate('/dashboard/dash_board');
      }
    } catch (error) {
      toast.error(error.message);
      setData({ email: '', password: '' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main className="auth-page">
      <button className="theme-fab" type="button" onClick={toggleTheme} aria-label="Toggle color theme">
        {isDarkMode ? <HiOutlineSun aria-hidden="true" /> : <HiOutlineMoon aria-hidden="true" />}
      </button>

      <section className="auth-card">
        <div className="auth-brand">
          <img src={logo} alt="Event Planet" className="auth-logo" />
          <span className="badge rounded-pill text-bg-primary-subtle">Admin Console</span>
        </div>

        <div className="auth-copy">
          <h1>Welcome back</h1>
          <p>Sign in to manage categories, publish blogs, and keep content moving.</p>
        </div>

        <form className="auth-form" onSubmit={loginHandler} noValidate>
          <div className="mb-3">
            <label className="form-label" htmlFor="login-email">
              Email address
            </label>
            <div className="input-group input-group-modern">
              <span className="input-group-text">
                <HiOutlineEnvelope aria-hidden="true" />
              </span>
              <input
                type="email"
                className={`form-control form-control-lg ${submitted && errors.email ? 'is-invalid' : ''}`}
                id="login-email"
                placeholder="you@example.com"
                name="email"
                onChange={formHandler}
                value={data.email}
                autoComplete="email"
              />
              {submitted && errors.email && <div className="invalid-feedback">{errors.email}</div>}
            </div>
          </div>

          <div className="mb-3">
            <label className="form-label" htmlFor="login-password">
              Password
            </label>
            <div className="input-group input-group-modern">
              <span className="input-group-text">
                <HiOutlineLockClosed aria-hidden="true" />
              </span>
              <input
                type={showPassword ? 'text' : 'password'}
                className={`form-control form-control-lg ${submitted && errors.password ? 'is-invalid' : ''}`}
                id="login-password"
                placeholder="Enter password"
                name="password"
                onChange={formHandler}
                value={data.password}
                autoComplete="current-password"
              />
              <button
                className="btn btn-input-action"
                type="button"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                onClick={() => setShowPassword((currentValue) => !currentValue)}
              >
                {showPassword ? <HiOutlineEyeSlash aria-hidden="true" /> : <HiOutlineEye aria-hidden="true" />}
              </button>
              {submitted && errors.password && <div className="invalid-feedback">{errors.password}</div>}
            </div>
          </div>

          <button className="btn btn-primary btn-lg w-100" type="submit" disabled={isSubmitting}>
            {isSubmitting ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" aria-hidden="true" />
                Signing in
              </>
            ) : (
              'Sign in'
            )}
          </button>
        </form>

        <p className="auth-footnote">
          Need an account? <Link to="/signup">Create one</Link>
        </p>
      </section>
    </main>
  );
};

export default Login;
